import { useEffect, useRef, useCallback, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { getAuthToken } from '../Services/AuthService';
import type {
  RealtimeConnectionState,
  RosterPersonStatusChangedEvent,
  IncidentClosedEvent,
  IncidentRefreshRequestedEvent,
  IncidentHeadcountCountsChangedEvent,
  IncidentRosterPersonAddedEvent,
} from '../realtime/incidentRealtimeTypes';
import { isValidHeadcountStatus, normalizeHeadcountStatus } from '../realtime/incidentRealtimeHelpers';

const HUB_PATH = '/hubs/incidents';

/**
 * Backoff delays (ms) for restarting the connection from scratch after
 * the built-in automatic reconnect has been exhausted.
 */
const RESTART_BACKOFF_MS = [5_000, 10_000, 30_000, 60_000];

export interface UseIncidentRealtimeOptions {
  incidentId: string | undefined;
  enabled?: boolean;
  onRosterPersonStatusChanged?: (event: RosterPersonStatusChangedEvent) => void;
  onIncidentClosed?: (event: IncidentClosedEvent) => void;
  onIncidentRefreshRequested?: (event: IncidentRefreshRequestedEvent) => void;
  onIncidentHeadcountCountsChanged?: (event: IncidentHeadcountCountsChangedEvent) => void;
  onIncidentRosterPersonAdded?: (event: IncidentRosterPersonAddedEvent) => void;
  onReconnect?: () => void;
  onConnectionStateChanged?: (state: RealtimeConnectionState) => void;
}

/**
 * Hook to manage a SignalR connection to the EEAS Incident Hub.
 *
 * - Connects when `incidentId` is set and `enabled` is true (default).
 * - Joins the incident group via `JoinIncident`.
 * - Leaves and disconnects on unmount or `incidentId` change.
 * - Automatically reconnects and re-joins.
 */
export const useIncidentRealtime = (options: UseIncidentRealtimeOptions) => {
  const {
    incidentId,
    enabled = true,
  } = options;

  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>('disconnected');
  const connectionRef = useRef<HubConnection | null>(null);
  const incidentIdRef = useRef<string | undefined>(incidentId);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Stable callback so we don't recreate the connection on every render
  const updateState = useCallback((state: RealtimeConnectionState) => {
    setConnectionState(state);
    optionsRef.current.onConnectionStateChanged?.(state);
  }, []);

  useEffect(() => {
    incidentIdRef.current = incidentId;
  }, [incidentId]);

  useEffect(() => {
    if (!incidentId || !enabled) {
      // If there's an existing connection, clean it up
      const existing = connectionRef.current;
      if (existing) {
        const prevId = incidentIdRef.current;
        connectionRef.current = null;
        (async () => {
          try {
            if (prevId && existing.state === HubConnectionState.Connected) {
              await existing.invoke('LeaveIncident', prevId);
            }
          } catch { /* ignore leave errors during cleanup */ }
          try { await existing.stop(); } catch { /* ignore */ }
        })();
        updateState('disconnected');
      }
      return;
    }

    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
    const hubUrl = `${apiBaseUrl}${HUB_PATH}`;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const token = await getAuthToken();
          return token || '';
        },
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(
        process.env.NODE_ENV === 'development' ? LogLevel.Information : LogLevel.Warning,
      )
      .build();

    connectionRef.current = connection;

    // Track whether this effect instance has been cleaned up (React Strict Mode
    // in development runs effects twice — the first is mounted then immediately
    // unmounted, which aborts the in-flight negotiate request).
    let aborted = false;

    // ── Register event handlers (reused for initial + restarted connections) ──

    const registerEventHandlers = (conn: HubConnection) => {
      conn.on('RosterPersonStatusChanged', (event: RosterPersonStatusChangedEvent) => {
        if (aborted) return;
        if (event.incidentId !== incidentIdRef.current) {
          console.debug('[realtime] Ignoring event for different incidentId:', event.incidentId);
          return;
        }
        if (!isValidHeadcountStatus(event.effectiveHeadcountStatus)) {
          console.warn('[realtime] Ignoring event with invalid effectiveHeadcountStatus:', event.effectiveHeadcountStatus);
          return;
        }
        // Normalize numeric status (e.g. 1) to string (e.g. 'accounted') before forwarding
        const normalized = {
          ...event,
          effectiveHeadcountStatus: normalizeHeadcountStatus(event.effectiveHeadcountStatus),
        };
        optionsRef.current.onRosterPersonStatusChanged?.(normalized);
        // Notify sidebar nav to refresh the Not Accounted count immediately
        window.dispatchEvent(new Event('eeas:statusChanged'));
      });

      conn.on('IncidentClosed', (event: IncidentClosedEvent) => {
        if (aborted || event.incidentId !== incidentIdRef.current) return;
        optionsRef.current.onIncidentClosed?.(event);
      });

      conn.on('IncidentRefreshRequested', (event: IncidentRefreshRequestedEvent) => {
        if (aborted || event.incidentId !== incidentIdRef.current) return;
        optionsRef.current.onIncidentRefreshRequested?.(event);
      });

      conn.on('IncidentHeadcountCountsChanged', (event: IncidentHeadcountCountsChangedEvent) => {
        if (aborted || event.incidentId !== incidentIdRef.current) return;
        optionsRef.current.onIncidentHeadcountCountsChanged?.(event);
      });

      conn.on('IncidentRosterPersonAdded', (event: IncidentRosterPersonAddedEvent) => {
        if (aborted || event.incidentId !== incidentIdRef.current) return;
        optionsRef.current.onIncidentRosterPersonAdded?.(event);
      });

      // ── Reconnect handler ──

      conn.onreconnecting(() => {
        if (!aborted) updateState('reconnecting');
      });

      conn.onreconnected(async () => {
        if (aborted) return;
        updateState('connected');
        // Re-join the incident group after reconnect
        const currentId = incidentIdRef.current;
        if (currentId) {
          try {
            await conn.invoke('JoinIncident', currentId);
            console.debug('[realtime] Re-joined incident after reconnect:', currentId);
          } catch (err) {
            console.error('[realtime] Failed to re-join incident after reconnect:', err);
          }
        }
        // Trigger full refresh callback
        optionsRef.current.onReconnect?.();
      });

      conn.onclose((err) => {
        if (aborted) return;
        if (err) {
          console.error('[realtime] Connection closed with error:', err);
          updateState('reconnecting');
          // Schedule a full restart — automatic reconnect has been exhausted
          scheduleRestart();
        } else {
          updateState('disconnected');
        }
      });
    };

    registerEventHandlers(connection);

    // ── Restart-from-scratch after permanent close ──

    let restartAttempt = 0;
    let restartTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRestart = () => {
      if (aborted) return;
      const delay = RESTART_BACKOFF_MS[Math.min(restartAttempt, RESTART_BACKOFF_MS.length - 1)];
      restartAttempt++;
      console.debug(`[realtime] Scheduling connection restart in ${delay}ms (attempt ${restartAttempt})`);
      restartTimer = setTimeout(() => {
        if (aborted) return;
        restartConnection();
      }, delay);
    };

    const restartConnection = async () => {
      if (aborted) return;
      updateState('reconnecting');

      // Build a fresh connection with the same config
      const freshConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await getAuthToken();
            return token || '';
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(
          process.env.NODE_ENV === 'development' ? LogLevel.Information : LogLevel.Warning,
        )
        .build();

      // Re-register all event handlers on the fresh connection
      registerEventHandlers(freshConnection);
      connectionRef.current = freshConnection;

      try {
        await freshConnection.start();
        if (aborted) {
          freshConnection.stop().catch(() => {});
          return;
        }
        updateState('connected');
        restartAttempt = 0; // reset backoff on success

        const currentId = incidentIdRef.current;
        if (currentId) {
          try {
            await freshConnection.invoke('JoinIncident', currentId);
            console.debug('[realtime] Re-joined incident after restart:', currentId);
          } catch (joinErr) {
            console.error('[realtime] Failed to join incident after restart:', joinErr);
          }
        }
        optionsRef.current.onReconnect?.();
      } catch (err) {
        if (aborted) return;
        console.error('[realtime] Restart attempt failed:', err);
        scheduleRestart();
      }
    };

    // ── Start connection ──

    const startConnection = async () => {
      updateState('connecting');
      try {
        await connection.start();
        // If cleanup ran while we were awaiting start(), bail out.
        if (aborted) {
          connection.stop().catch(() => {});
          return;
        }
        updateState('connected');

        // Join the incident group
        try {
          await connection.invoke('JoinIncident', incidentId);
          console.debug('[realtime] Joined incident:', incidentId);
        } catch (joinErr) {
          console.error('[realtime] Failed to join incident:', joinErr);
          if (!aborted) updateState('error');
        }
      } catch (err) {
        // "stopped during negotiation" is expected in Strict Mode — suppress it.
        if (aborted) return;
        console.error('[realtime] Failed to start connection:', err);
        updateState('error');
      }
    };

    // Defer start so React Strict Mode's immediate cleanup can cancel it
    // before the negotiate HTTP request is even sent.
    const startTimer = setTimeout(() => {
      if (!aborted) startConnection();
    }, 0);

    // ── Cleanup on unmount or incidentId change ──
    return () => {
      aborted = true;
      clearTimeout(startTimer);
      if (restartTimer) clearTimeout(restartTimer);
      // Stop whichever connection is active (may be the original or a restarted one)
      const activeConnection = connectionRef.current;
      connectionRef.current = null;
      const currentId = incidentId;
      const stopConnection = (conn: HubConnection) => {
        (async () => {
          try {
            if (currentId && conn.state === HubConnectionState.Connected) {
              await conn.invoke('LeaveIncident', currentId);
              console.debug('[realtime] Left incident:', currentId);
            }
          } catch { /* ignore leave errors */ }
          try { await conn.stop(); } catch { /* ignore */ }
        })();
      };
      // Stop the active connection (may differ from the original after restart)
      if (activeConnection && activeConnection !== connection) {
        stopConnection(activeConnection);
      }
      stopConnection(connection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId, enabled, updateState]);

  return { connectionState };
};
